import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { buildQueryFilters, useGetChildOrganizations } from '@sib/api';
import { OrganizationDto } from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Skeleton,
} from '@sib/ui';
import { ChevronDown, Check, TeamOrg } from '@sib/ui/icons';
import { useUploadQueueGuard } from '@spendbrain/common/hooks';
import { paths } from '@spendbrain/config/paths';

export interface OrganizationSwitchButtonProps {
  organization: OrganizationDto | undefined;
  organizationId: string;
  setOrganizationId: (id: string, name: string) => void;
  setParentOrganization: (org: OrganizationDto | null) => void;
  entryPointOrganization: OrganizationDto;
  setEntryPointOrganization: (org: OrganizationDto | null) => void;
}

export enum OrgType {
  Parent = 'parent',
  Subsidiary = 'subsidiary',
}

interface DropdownOrgItem {
  id: string;
  name: string;
  type: OrgType;
}

export function OrganizationSwitchButton({
  organization,
  organizationId,
  setOrganizationId,
  setParentOrganization,
  entryPointOrganization,
  setEntryPointOrganization,
}: OrganizationSwitchButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { requestAction } = useUploadQueueGuard();

  const { data: childData, isLoading } = useGetChildOrganizations(
    entryPointOrganization.id,
    {
      page: DEFAULT_PAGE,
      size: MAXIMUM_PAGINATION,
      filter: buildQueryFilters([
        { field: 'micrositesEnabled', operator: 'eq', value: true },
      ]),
    }
  );

  const subsidiaryOrgs = useMemo<DropdownOrgItem[]>(
    () =>
      childData?.items?.map(org => ({
        id: org.id,
        name: org.name,
        type: OrgType.Subsidiary,
      })) ?? [],
    [childData]
  );

  const parentOrg = useMemo<DropdownOrgItem[]>(
    () => [
      {
        id: entryPointOrganization.id,
        name: entryPointOrganization.name,
        type: OrgType.Parent,
      },
    ],
    [entryPointOrganization]
  );

  const selectedOrg = useMemo<DropdownOrgItem | undefined>(() => {
    if (!organization) return undefined;

    return {
      id: organization.id,
      name: organization.name,
      type:
        organization.id === entryPointOrganization.id
          ? OrgType.Parent
          : OrgType.Subsidiary,
    };
  }, [organization, entryPointOrganization]);

  const applyOrganizationChange = useCallback(
    (org: DropdownOrgItem) => {
      setOrganizationId(org.id, org.name);
      const isSubsidiary = org.type === OrgType.Subsidiary;
      setParentOrganization(isSubsidiary ? (organization ?? null) : null);
      if (org.id === entryPointOrganization.id) {
        setEntryPointOrganization(null);
      }
      navigate(paths.dashboard);
    },
    [
      setOrganizationId,
      setParentOrganization,
      organization,
      entryPointOrganization,
      setEntryPointOrganization,
      navigate,
    ]
  );

  const handleSelect = useCallback(
    (org: DropdownOrgItem) => {
      if (org.id === organizationId) return;

      requestAction(() => applyOrganizationChange(org));
    },
    [organizationId, requestAction, applyOrganizationChange]
  );

  const renderItem = useCallback(
    (org: DropdownOrgItem) => (
      <DropdownMenuItem
        key={org.id}
        onClick={() => handleSelect(org)}
        className={cn(
          'flex cursor-pointer items-center justify-between gap-2 border-none px-2 py-1.5',
          org.type === OrgType.Parent && 'pt-0'
        )}
      >
        <span className={cn(org.type === OrgType.Parent && 'font-medium')}>
          {org.name}
        </span>
        {selectedOrg?.id === org.id && (
          <Check className="text-primary h-4 w-4" />
        )}
      </DropdownMenuItem>
    ),
    [handleSelect, selectedOrg]
  );

  if (isLoading || !organization) {
    return <Skeleton className="h-[32px] w-[120px]" />;
  }

  const translationKey = (org: DropdownOrgItem, short = false) =>
    t(`components.organizationSwitchButton.${org.type}${short ? 'Short' : ''}`);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <TeamOrg className="text-primary h-5 w-5" />
          <span className="hidden sm:inline">{selectedOrg?.name}</span>
          <span className="sm:hidden">
            {selectedOrg ? translationKey(selectedOrg, true) : ''}
          </span>
          <ChevronDown className="text-muted-foreground ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="p-1">
        <DropdownMenuLabel className="text-muted-foreground px-2 pb-0 text-xs font-normal">
          {t('components.organizationSwitchButton.parent')}
        </DropdownMenuLabel>
        {parentOrg.map(renderItem)}
        {subsidiaryOrgs.map(renderItem)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
